import { z } from 'zod'

export const UserSettingsResponseSchema = z.object({
  notify_on_project_completion: z.boolean().optional(),
  experimental_settings: z.record(z.string(), z.boolean()).optional(),
  welcome_pending: z.boolean().optional(),
}).passthrough()

export const SubscriptionResponseSchema = z.object({
  subscription: z.object({
    plan: z.object({ key: z.string(), name: z.string() }).passthrough(),
    status: z.string(),
    currentPeriodEnd: z.number().nullable(),
    cancelAtPeriodEnd: z.boolean(),
    seatCount: z.number(),
  }).passthrough().nullable(),
  workspaceId: z.string().nullable().optional(),
}).passthrough()

// A one-off credit pack on sale, as /api/stripe-credit-packs relays it from Stripe. An empty list is a
// normal answer: it means nothing is on sale, and the credit meter offers a conversation instead.
export const CreditPackSchema = z.object({
  priceId: z.string(),
  credits: z.number(),
  amountCents: z.number(),
  currency: z.string(),
  name: z.string(),
}).passthrough()

export const CreditPacksResponseSchema = z.object({
  packs: z.array(CreditPackSchema),
}).passthrough()

export const CreditTopUpResponseSchema = z.object({
  url: z.string(),
}).passthrough()

export type CreditPacksResponse = z.infer<typeof CreditPacksResponseSchema>

const ProjectUsageTaskSchema = z.object({
  task_id: z.string(),
  kind: z.enum(['exploration', 'journey', 'task']),
  label: z.string(),
  date: z.string(),
  credits: z.number(),
}).passthrough()

export const ProjectUsageSchema = z.object({
  project_id: z.string(),
  project_name: z.string(),
  total_credits: z.number(),
  refunded_credits: z.number().optional(),
  qa_costs_refunded: z.boolean().optional(),
  // Optional: the team-level /api/usage response ships per-project aggregates only — the per-task
  // rows come with the /api/usage?project_id= drill-down (QA bug bug-mtxawj2w-411z).
  tasks: z.array(ProjectUsageTaskSchema).optional(),
}).passthrough()

const TeamCreditSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  role: z.string(),
  is_owner: z.boolean(),
  member_count: z.number(),
  available_credits: z.number(),
  // Optional so a response from a deploy before team funding shipped still parses.
  monthly_credits: z.number().optional(),
  plan_key: z.string().nullable().optional(),
  can_invite: z.boolean().optional(),
  owner_name: z.string().nullable().optional(),
  owner_email: z.string().nullable().optional(),
}).passthrough()

export const UsageDataSchema = z.object({
  available_credits: z.number(),
  total_used: z.number(),
  projects: z.array(ProjectUsageSchema),
  usage_waived: z.boolean().optional(),
  usage_code: z.string().nullable().optional(),
  usage_code_ends_at: z.string().nullable().optional(),
  // Which team the balance belongs to, and every team the user could switch to. Optional so a
  // response from before teams (or a mock that omits them) still parses.
  team_id: z.string().nullable().optional(),
  team_name: z.string().nullable().optional(),
  teams: z.array(TeamCreditSummarySchema).optional(),
}).passthrough()

export const API_TOKEN_NAME_MAX_LENGTH = 100

export const CreateApiTokenInputSchema = z.object({
  name: z.string().trim().min(1, 'Enter a token name').max(API_TOKEN_NAME_MAX_LENGTH, 'Token names must be 100 characters or fewer'),
}).strict()

export const DeleteApiTokenInputSchema = z.object({
  id: z.string().trim().min(1, 'A token ID is required').max(200),
}).strict()

export const ApiTokenInfoSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  name: z.string(),
  created_at: z.string(),
})

// Tolerate the pre-"multiple named tokens" (#3390) response shape, which carried only the singular
// `token` metadata field and no `tokens` array. A backend on the old contract — a not-yet-rolled deploy,
// or a replayed capture recorded before #3390 — must still leave the token page usable rather than fail
// the whole query and strand it on "Unable to load your tokens"; an absent array means no tokens.
export const ApiTokensResponseSchema = z.object({
  tokens: z.array(ApiTokenInfoSchema).default([]),
})

// The created-token twin of ApiTokensResponseSchema's tolerance above: the pre-"multiple named tokens"
// (#3390) response carried no `name`. A backend on the old contract — a not-yet-rolled deploy, or a
// replayed capture recorded before #3390 — must still let the created token display rather than fail
// the mutation and strand the flow on "Unable to create a token" after the token was minted; an absent
// name means an unnamed token.
export const GeneratedApiTokenSchema = z.object({
  token: z.string(),
  id: z.string(),
  name: z.string().default(''),
  created_at: z.string(),
})

export const ApiTokenRequestErrorSchema = z.object({
  data: z.object({ error: z.string() }),
})
